"use client";

import Link from "next/link";
import { Piano, RotateCw } from "lucide-react";
import { useLiveSession } from "@/contexts/live-session-context";
import { useReplay } from "@/contexts/replay-context";

/**
 * Calque qui remplace le studio sur téléphone, dans les deux orientations.
 *
 * **Pourquoi il ne demande plus de tourner l'appareil.** La version précédente
 * affichait « Tourne ton téléphone » en portrait. Le conseil était faux : en
 * paysage, un téléphone offre 390 px de haut, dont 84 partent en header et en
 * toolbar ; il reste environ 150 px de course aux notes une fois le clavier
 * posé. On faisait donc pivoter l'utilisateur vers un écran qui ne devient pas
 * jouable pour autant. Le téléphone n'est pas un support de jeu dégradé, c'est
 * une surface de découverte, et l'écran doit le dire.
 *
 * **Les deux conditions d'affichage sont écrites en CSS, pas en JS.** Tout hook
 * de media query (voir {@link ../../hooks/use-media-query}) annonce `false` au
 * premier rendu : brancher ce calque dessus le ferait clignoter une frame sur
 * desktop, exactement l'accident qu'on ne peut pas se permettre ici.
 *
 * - `portrait:max-md:pointer-coarse:` attrape le téléphone debout. `md` vaut
 *   48 rem, la valeur de `BREAKPOINTS.medium` ; les deux doivent bouger
 *   ensemble.
 * - `[@media(max-height:480px)]:pointer-coarse:` attrape le téléphone couché,
 *   que la largeur ne sait pas voir : à 844 px de large il dépasse `md` et
 *   passerait pour une tablette. C'est le seuil de
 *   {@link ../../hooks/use-short-viewport}, où le raisonnement est écrit :
 *   sous 480 px de haut il n'y a que des téléphones en paysage.
 *
 * **`pointer-coarse:` n'est décoratif dans aucune des deux.** `orientation` et
 * `max-height` décrivent le viewport, pas l'appareil : une fenêtre desktop
 * étroite, ou simplement basse, y répond « oui ». Sans le pointeur grossier, on
 * couvrirait le studio de quelqu'un qui n'a qu'à redimensionner sa fenêtre.
 *
 * **Ce n'est qu'un calque.** `StudioPage` reste monté dessous (singleton, pour
 * ne pas re-télécharger les samples), donc l'état et la lecture survivent. Le
 * calque s'arrête sous le header, qui reste le chemin de sortie.
 *
 * **La seule condition en JS est celle du spectateur.** Elle ne dépend pas du
 * viewport mais d'un état, et cet état ne clignote pas : il vaut « off » tant
 * que l'URL ne porte pas de salle, et la salle se lit dans l'URL dès le premier
 * rendu. Le calque doit donc vivre sous le fournisseur live pour lire le
 * billet — c'est pour ça que le layout le rend là.
 */
export function HandheldNotice() {
  const live = useLiveSession();
  const replay = useReplay();

  // Un téléphone en paysage regarde très bien un rouleau : ce que le calque
  // refuse — poser les deux mains sur une octave et demie — n'est pas ce qu'un
  // spectateur vient faire. Il cède donc la place dès qu'une séance est en
  // cours sans que le billet fasse de nous un joueur : spectateur, ou rôle
  // encore inconnu pendant la connexion — se montrer entre-temps ferait
  // clignoter le studio à l'entrée d'un récital. Le joueur et l'interprète,
  // eux, le gardent : il leur faut un instrument, et le calque le dit.
  //
  // Une jointure en erreur n'est pas une séance en cours : le studio nu n'a
  // rien à montrer à un téléphone, et le calque revient dire ce qu'il dit.
  const joiningOrOn =
    live !== null &&
    live.status !== "off" &&
    live.status !== "error";

  // Devant un replay, le calque n'a plus rien à dire, dans aucune des deux
  // orientations.
  //
  // Couché, c'était déjà le cas. Debout, on demandait de tourner — et le
  // conseil était devenu faux pour la même raison qu'il l'était en paysage
  // pour le studio de jeu : il envoyait vers l'orientation qui convient à un
  // *clavier*, alors qu'on est venu regarder un *rouleau*. Un rouleau tombe
  // dans le sens de la hauteur : les 740 px d'un téléphone debout en montrent
  // deux fois et demie plus que les 258 px du même téléphone couché. Ce qu'on
  // y perd est l'étendue de clavier, et devant un enregistrement le clavier
  // n'est qu'un témoin — voir `lib/studio/view/witness-keyboard.ts`, où la
  // largeur de touche descend en conséquence.
  //
  // Le conseil reste entier pour le spectateur d'un récital **en direct** :
  // rien n'a encore été mesuré de ce côté, et son écran porte des choses que
  // le replay n'a pas.
  if (replay.recitalId !== null) {
    return null;
  }

  if (joiningOrOn && live.role !== "player" && live.role !== "performer") {
    return <SpectatorNotice />;
  }

  return (
    <div
      className="
        absolute inset-0 z-40 hidden
        portrait:max-md:pointer-coarse:flex
        [@media(max-height:480px)]:pointer-coarse:flex
        flex-col items-center justify-center gap-5
        [@media(max-height:480px)]:gap-3
        overflow-y-auto px-8 py-6 text-center bg-bg-deep
      "
    >
      <Piano
        aria-hidden="true"
        size={38}
        strokeWidth={1.25}
        className="shrink-0 text-accent-bright [@media(max-height:480px)]:hidden"
      />

      <h2 className="text-[26px] [@media(max-height:480px)]:text-[20px] font-[250] tracking-[-0.02em] text-text-primary">
        Virtuosa se joue devant un piano
      </h2>

      <p className="max-w-[38ch] text-[13.5px] leading-[1.65] text-text-secondary">
        Il te faut ton instrument, et un écran assez large pour tenir sur le
        pupitre : une tablette ou un ordinateur. Sur un téléphone, le clavier se
        réduit à une octave et demie, de quoi regarder les notes tomber, pas de
        quoi poser les deux mains.
      </p>

      {/*
        Une porte, pas un cul-de-sac. Les fiches de morceaux sont rendues côté
        serveur et se lisent très bien sur un téléphone : c'est ce qu'on a envie
        de faire quand on découvre le produit loin de son piano.
      */}
      <div className="flex flex-col items-center gap-3 [@media(max-height:480px)]:gap-2">
        <Link
          href="/library"
          className="text-[14px] text-accent-bright border-b border-dashed border-accent-dim pb-0.5 transition-colors hover:text-text-primary"
        >
          Parcourir la bibliothèque
        </Link>

        <Link
          href="/learn"
          className="text-[13px] text-text-muted transition-colors hover:text-text-secondary"
        >
          Voir les programmes
        </Link>
      </div>

      {/*
        Couture pour la suite : c'est ici que viendra le « envoie-toi le lien »,
        un champ e-mail qui permet de retrouver Virtuosa une fois devant le
        piano. Rien n'est posé pour l'instant parce que l'API n'expose aucune
        capture d'adresse : un champ qui ne mène nulle part serait pire que son
        absence. Le jour où l'endpoint existe, il s'insère à cet endroit sans
        toucher au reste du calque.
      */}
    </div>
  );
}

/**
 * Ce que voit le spectateur d'un récital sur un téléphone tenu debout.
 *
 * Seulement debout : couché, le rouleau occupe toute la largeur et se
 * regarde très bien — c'est la seule des deux conditions du calque principal
 * qui tombe ici. Debout, la barre d'outils du studio se replie sur elle-même
 * et le rouleau ne fait qu'une poignée d'octaves : plutôt que de le montrer
 * cassé, on demande le geste qui le répare. Pas de porte de sortie vers la
 * bibliothèque : la personne est venue regarder quelqu'un jouer, et c'est
 * derrière ce calque que ça se passe.
 */
function SpectatorNotice() {
  return (
    <div
      className="
        absolute inset-0 z-40 hidden
        portrait:max-md:pointer-coarse:flex
        flex-col items-center justify-center gap-5
        px-8 py-6 text-center bg-bg-deep
      "
    >
      <RotateCw
        aria-hidden="true"
        size={34}
        strokeWidth={1.25}
        className="shrink-0 text-accent-bright"
      />

      <h2 className="text-[24px] font-[250] tracking-[-0.02em] text-text-primary">
        Tourne ton téléphone
      </h2>

      <p className="max-w-[34ch] text-[13.5px] leading-[1.65] text-text-secondary">
        Le récital se regarde en paysage : le rouleau prend alors toute la
        largeur de l&apos;écran, et les notes ont la place de tomber.
      </p>
    </div>
  );
}
